import React, { useState } from 'react';
import { Activity, ArrowRight, Plus, Minus, ClipboardList, Sparkles, Stethoscope, Users } from 'lucide-react';
import RoadmapEarlyAccess from '@/components/landing/RoadmapEarlyAccess';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import MarketingImage from '@/components/landing/MarketingImage';
import { SITE_URL } from '@/data/blogSeoMetadata';
import {
  COMPARISON_CONCLUSION,
  COMPARISON_DESCRIPTION,
  COMPARISON_HEADING,
  COMPARISON_INTRO,
  COMPARISON_PAGE_PATH,
  COMPARISON_PAGE_TITLE,
  COMPARISON_ROWS,
} from '@/data/nutritionSoftwareComparison';
import { PNP_FAQS, PNP_PAGE_PATH } from '@/data/personalisedNutritionSeo';
import { usePublicSeo } from '@/marketing/usePublicSeo';
import base from './PersonalisedNutritionPage.module.css';
import styles from './NutritionSoftwareComparisonPage.module.css';

const canonicalUrl = `${SITE_URL}${COMPARISON_PAGE_PATH}`;
const schemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${canonicalUrl}#faq`,
    mainEntity: PNP_FAQS.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: COMPARISON_HEADING,
    description: COMPARISON_DESCRIPTION,
    url: canonicalUrl,
    inLanguage: 'en-GB',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Nutrition Software Comparison', item: canonicalUrl },
    ],
  },
];

const FAQ_GROUPS = [
  { title: 'Personalised nutrition', description: 'The science, the approach and your role.', indices: [0, 1, 2, 4, 8] },
  { title: 'Using CalorieScience', description: 'The platform, your practice and what’s next.', indices: [3, 5, 6, 7, 9] },
];

const FaqAccordion: React.FC = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className={styles.faqGroups}>
      {FAQ_GROUPS.map(group => (
        <div className={styles.faqGroup} key={group.title}>
          <div className={styles.faqGroupHeading}>
            <div><h3>{group.title}</h3><p>{group.description}</p></div>
          </div>
          <div className={styles.faqList}>
            {group.indices.map(index => {
              const faq = PNP_FAQS[index];
              const isOpen = openIndex === index;
              return (
                <article className={styles.faqItem} data-open={isOpen} key={faq.question}>
                  <h4>
                    <button
                      type="button"
                      className={styles.faqQuestion}
                      onClick={() => setOpenIndex(isOpen ? -1 : index)}
                      aria-expanded={isOpen}
                      aria-controls={`pnp-faq-answer-${index}`}
                      id={`pnp-faq-question-${index}`}
                    >
                      <span>{faq.question}</span>
                      <span className={styles.faqIcon} aria-hidden="true">
                        {isOpen ? <Minus size={17} /> : <Plus size={17} />}
                      </span>
                    </button>
                  </h4>
                  <div id={`pnp-faq-answer-${index}`} aria-labelledby={`pnp-faq-question-${index}`} hidden={!isOpen}>
                    <p className={styles.faqAnswer}>{faq.answer}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function NutritionSoftwareComparisonPage() {
  usePublicSeo(COMPARISON_PAGE_PATH, COMPARISON_PAGE_TITLE, COMPARISON_DESCRIPTION, schemas);

  return (
    <div className={base.page} id="top">
      <div className={base.heroBackdrop}>
        <Navbar />
      </div>
      <main>
        <header className={`${base.heroBackdrop} ${styles.hero}`}>
          <div className={base.inner}>
            <p className={base.breadcrumb}>
              <Link to="/">Home</Link><span>/</span>Nutrition Software Comparison
            </p>
            <div className={styles.heroCopy}>
              <span className={base.kicker}>Built for connected care</span>
              <h1 className={base.heroTitle}>{COMPARISON_HEADING}</h1>
              <p className={base.sectionText}>{COMPARISON_INTRO}</p>
              <a href="#comparison" className={base.primaryButton}>
                Compare the features <ArrowRight size={17} aria-hidden="true" />
              </a>
            </div>
          </div>
        </header>

        <section className={base.section} id="comparison" aria-labelledby="comparison-title">
          <div className={base.inner}>
            <div className={base.sectionHead}>
              <span className={base.kicker}>At a glance</span>
              <h2 className={base.sectionTitle} id="comparison-title">Compare nutrition software</h2>
            </div>
            <div className={base.tableWrap} role="region" aria-label="Nutrition software comparison" tabIndex={0}>
              <table className={base.comparisonTable}>
                <caption className="sr-only">CalorieScience, nutrition analysis software and practice-management platforms compared across six features.</caption>
                <thead>
                  <tr>
                    <th scope="col">Feature</th>
                    <th scope="col">Nutrition analysis software</th>
                    <th scope="col">Practice-management platforms</th>
                    <th scope="col">
                      <span className={base.tableBrand}>
                        <MarketingImage src="/images/marketing/lovable-uploads-cc1c4082-b52d-4f93-9c6a-0bc389e4be79.webp" alt="" />
                        CalorieScience
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map(row => (
                    <tr key={row.label}>
                      <th scope="row" className={base.rowLabel}>{row.label}</th>
                      <td>{row.traditional}</td>
                      <td>{row.practice}</td>
                      <td className={base.csCell}>{row.cs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className={styles.conclusion}>{COMPARISON_CONCLUSION}</p>
            <Link className={styles.platformLink} to={PNP_PAGE_PATH}>
              Explore the personalised nutrition platform <ArrowRight size={17} aria-hidden="true" />
            </Link>
          </div>
        </section>

        {/* Why choose / who for */}
        <section className={`${base.section} ${base.sectionAlt}`} id="who-its-for">
          <div className={`${base.inner} ${base.audienceLayout}`}>
            <div className={base.sectionHead}>
              <span className={base.kicker}>Designed for your practice</span>
              <h2 className={base.sectionTitle}>Who CalorieScience is for</h2>
            </div>
            <div className={base.subBlock}>
              <ul className={base.audienceList}>
                <li><Stethoscope aria-hidden="true" /><span>Registered dietitians in private practice, clinics and community settings</span></li>
                <li><Activity aria-hidden="true" /><span>Registered nutritionists</span></li>
                <li><ClipboardList aria-hidden="true" /><span>Nutritional therapists who use detailed case-taking and testing</span></li>
                <li><Users aria-hidden="true" /><span>Multidisciplinary teams aligning food and lifestyle support with medical care plans</span></li>
              </ul>
              <p className={base.disclaimerNote}>
                CalorieScience is a professional tool. It does not replace medical advice, diagnosis or treatment. Clients should consult their GP or specialist about medical conditions and medication changes.
              </p>
            </div>
            <div className={base.onboardingPanel}>
              <Sparkles size={28} aria-hidden="true" />
              <h3>What happens after signup?</h3>
              <p>
                Create your account, add your first client's intake and any blood results, and generate your first AI-assisted, practitioner-approved meal plan, usually in your first session.
              </p>
            </div>
          </div>
        </section>

        <RoadmapEarlyAccess />

        {/* Post CTA */}
        <section className={`${base.section} ${base.finalSection}`} id="get-started">
          <div className={`${base.inner} ${base.finalPanel}`}>
            <div className={base.sectionHead}>
              <span className={base.kicker}>Make it personal</span>
              <h2 className={base.sectionTitle}>
                See CalorieScience <em>in action</em>
              </h2>
              <p className={base.sectionText}>The AI-assisted, GDPR-compliant nutrition platform built for UK and EU practitioners.</p>
            </div>
            <div className={base.heroCtas} style={{ justifyContent: 'center' }}>
              <a href="/signup" className={base.primaryButton} data-analytics-event="Landing CTA Clicked" data-analytics-id="comparison_start_trial" data-analytics-section="nutrition_software_comparison">
                Start free trial <ArrowRight size={17} aria-hidden="true" />
              </a>
              <a
                href="mailto:info@caloriescience.ai"
                className={base.secondaryButton}
                data-analytics-event="Landing CTA Clicked"
                data-analytics-id="comparison_talk_to_us"
                data-analytics-section="nutrition_software_comparison"
              >
                Talk to us
              </a>
            </div>
            <p className={base.disclosure}>
              CalorieScience is a workflow and education platform that supports practitioner judgement. It is UK and EU GDPR compliant. It is not a diagnostic tool or a medical device, and it does not replace clinical assessment by a qualified practitioner.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className={`${base.section} ${styles.faqSection}`} id="faq" aria-labelledby="faq-heading">
          <div className={base.inner}>
            <div className={styles.faqHeading}>
              <span className={base.kicker}>Frequently asked questions</span>
              <h2 className={base.sectionTitle} id="faq-heading">Your questions, <span>answered.</span></h2>
              <p className={base.sectionText}>Get to know personalised nutrition and how CalorieScience fits into your practice.</p>
            </div>
            <FaqAccordion />
            <div className={styles.faqContact}>
              <div><h3>Still have a question?</h3><p>Talk to our team about your practice and what you need.</p></div>
              <a href="mailto:info@caloriescience.ai" className={styles.faqContactLink}>info@caloriescience.ai <ArrowRight size={17} aria-hidden="true" /></a>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
